import {
    getAlphaEyeSettings,
    getAlphaLensDefaultWidget,
    getAlphaScansTabIds,
    getDefaultScannerPlanId,
    getIndicatorFormulasFromToolbarItems,
    getOrderEntryTickers,
    getPinnedUniverses,
    getSuggestedColumns,
    getToolbarItems,
    updateToolbarItemsList,
} from './http/userPreference';
export { updateToolbarItemsList };
import type { MutationObserverOptions, QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { DictionaryBackedData } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { OrderEntryTicker, ToolbarItem } from '@thinkalpha/platform-ws-client/contracts/user-preference.js';
import type { AlphaEyeSettings } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import type { TemplateColumnBase } from '@thinkalpha/ui-types/contracts/column-template';
import type { IndicatorFormulaViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type { AlphaLensContent } from '@thinkalpha/ui-types/contracts/workspace';
import type { IndicatorImportModel } from 'src/dtos/IndicatorImport';

// Constants
export const USER_PREFERENCES = 'user-preferences';
export const PINNED_UNIVERSES = 'pinned-universes';
export const TOOLBAR_ITEMS = 'toolbar-items';
export const ALPHA_LENS_WIDGET_MODEL_DEFAULT = 'alpha-lens-widget-model-default';
export const ALPHA_EYE_SETTINGS = 'alpha-eye-settings';
export const DEFAULT_INDICATOR_IMPORT_MODEL_FORMULAS = 'default-indicator-import-model-formulas';
export const ORDER_ENTRY_TICKERS = 'order-entry-tickers';
export const SUGGESTED_COLUMNS = 'suggested-columns';
export const DEFAULT_SCANNER_PLAN_ID = 'default-scanner-plan-id';
export const ALPHA_SCANS_TAB_IDS = 'alpha-scans-tab-ids';
/**
 * Get pinned universe ids for a user.
 */
export function getPinnedUniversesQuery(): QueryObserverOptions<string[]> {
    return {
        queryKey: [USER_PREFERENCES, PINNED_UNIVERSES],
        queryFn: () => getPinnedUniverses(),
    };
}

/**
 * Get default scanner plan ID for a user.
 */
export function getDefaultScannerPlanIdQuery(): QueryObserverOptions<string> {
    return {
        queryKey: [USER_PREFERENCES, DEFAULT_SCANNER_PLAN_ID],
        queryFn: () => getDefaultScannerPlanId(),
    };
}

/**
 * Get toolbar items for a user as raw contract types.
 */
export function getRawToolbarItemsQuery({ enabled = true }: { enabled?: boolean } = {}): QueryObserverOptions<
    ToolbarItem[]
> {
    return {
        queryKey: [USER_PREFERENCES, TOOLBAR_ITEMS, 'raw'],
        queryFn: () => getToolbarItems(),
        enabled,
    };
}

export function getToolbarMutationObserverOptions({
    queryClient,
    onError,
}: {
    queryClient: QueryClient;
    onError: (error: Error, payload: ToolbarItem[]) => void;
}): MutationObserverOptions<unknown, Error, ToolbarItem[], unknown> {
    const toolbarItemsQuery = getRawToolbarItemsQuery();
    return {
        mutationFn: (payload) => updateToolbarItemsList({ payload }),
        onSuccess: (_data, payload) => {
            queryClient.setQueryData<ToolbarItem[]>(toolbarItemsQuery.queryKey, payload);
        },
        onError: (error, payload) => {
            onError(error, payload);
        },
    };
}

export function getAlphaLensDefaultWidgetQuery(): QueryObserverOptions<AlphaLensContent> {
    return {
        queryKey: [USER_PREFERENCES, ALPHA_LENS_WIDGET_MODEL_DEFAULT],
        queryFn: () => getAlphaLensDefaultWidget(),
    };
}

export function getAlphaEyeSettingsQuery(): QueryObserverOptions<AlphaEyeSettings> {
    return {
        queryKey: [USER_PREFERENCES, ALPHA_EYE_SETTINGS],
        queryFn: () => getAlphaEyeSettings(),
    };
}

export function getIndicatorFormulasFromToolbarItemsQuery(
    defaultFormulas?: DictionaryBackedData[],
): QueryObserverOptions<IndicatorFormulaViewModel<IndicatorImportModel>[]> {
    return {
        queryFn: () => getIndicatorFormulasFromToolbarItems(defaultFormulas ?? []),
        queryKey: [USER_PREFERENCES, DEFAULT_INDICATOR_IMPORT_MODEL_FORMULAS, JSON.stringify(defaultFormulas ?? [])],
    };
}

export function getOrderEntryTickersQuery(): QueryObserverOptions<OrderEntryTicker[]> {
    return {
        queryFn: () => getOrderEntryTickers(),
        queryKey: [USER_PREFERENCES, ORDER_ENTRY_TICKERS],
    };
}

export function getAlphaScansTabIdsQuery(): QueryObserverOptions<string[]> {
    return {
        queryKey: [USER_PREFERENCES, ALPHA_SCANS_TAB_IDS],
        queryFn: () => getAlphaScansTabIds(),
    };
}

export function getSuggestedColumnsQuery(suggestedColumnsKey?: string): QueryObserverOptions<TemplateColumnBase[]> {
    return {
        queryKey: [USER_PREFERENCES, SUGGESTED_COLUMNS, suggestedColumnsKey],
        queryFn: () => getSuggestedColumns(suggestedColumnsKey!),
        enabled: !!suggestedColumnsKey,
    };
}
